import { useTheme } from '@mui/material';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { format } from 'date-fns';
import React from 'react';
import { useTranslation } from 'react-i18next';

import Button from '@/components/Button';
import { StatusChip } from '@/components/Cards/Chips/StatusChip';
import Modal from '@/components/Modals/Modal';
import useResponsive from '@/hooks/useResponsive';
import { IFalabellaOrder } from '@/types/plugins/falabella';

import {
  getFalabellaModalityLabel,
  getFalabellaStatusBg,
  getFalabellaStatusColor,
  getFalabellaStatusLabel,
} from '../../constants';

interface DetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  order: IFalabellaOrder | null;
  onDownloadLabel: (orderId: string) => void;
  onCreateOrder?: (orderId: string) => void;
  isCreating?: boolean;
}

interface PairTitleProps {
  title: string;
  value?: string;
  isBold?: boolean;
  isGreen?: boolean;
  isTitleGreen?: boolean;
  children?: React.ReactNode;
}

const PairTitle = (props: PairTitleProps) => {
  const { title, value, isBold, isGreen, isTitleGreen, children } = props;
  const theme = useTheme();
  return (
    <Box display="flex" flexDirection="column" alignItems="center" gap={1} textAlign="center">
      <Typography
        variant="body1"
        fontWeight={theme.typography.fontWeightMedium}
        sx={{ color: isTitleGreen ? theme.palette.primary.main : 'inherit' }}
      >
        {title}
      </Typography>
      {children ?? (
        <Typography
          variant="body1"
          sx={{
            fontWeight: isBold ? 'bold' : 'normal',
            wordBreak: 'break-all',
            color: isGreen ? theme.palette.primary.main : theme.palette.grey[800],
            textAlign: 'center',
          }}
        >
          {value || '-'}
        </Typography>
      )}
    </Box>
  );
};

const DetailsModal = (props: DetailsModalProps) => {
  const { isOpen, onClose, order, onDownloadLabel, onCreateOrder, isCreating } = props;
  const translation = useTranslation('falabella');
  const isTablet = useResponsive('up', 'lg');
  const theme = useTheme();

  if (!order) return null;

  const hasTracking = Boolean(order.tracking99m && order.tracking99m.trim().length > 0);
  const trackingId = order.tracking99m || '';
  const isDirect = (order.modality || order.shippingProviderType || '').toLowerCase() === 'direct';
  const cleanStatus = (order.statusFalabella || '').toLowerCase().trim();
  const rawInternalKey = (order.internalKey || order.lpn || '').trim();
  const hasInternalKey = Boolean(
    rawInternalKey && rawInternalKey !== order.orderFalabella && rawInternalKey !== order.id,
  );
  const isReadyToShip = cleanStatus === 'ready_to_ship';
  const isPending = cleanStatus === 'pending';
  const canCreatePending = isPending && hasInternalKey;
  const canCreate = isDirect && (isReadyToShip || canCreatePending);

  const cellStyleHeader: React.CSSProperties = {
    padding: '12px',
    fontWeight: 600,
    color: theme.palette.secondary.main,
    whiteSpace: 'nowrap',
  };

  const cellStyle: React.CSSProperties = {
    padding: '10px',
    color: '#444',
    backgroundColor: '#fafafa',
    whiteSpace: 'nowrap',
  };

  return (
    <Modal isOpen={isOpen} title="Detalle de orden" onClose={onClose} maxWidth="xl">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: isTablet ? '0' : '0 !important',
        }}
      >
        {order.rejectionReason && (
          <Box sx={{ width: '100%', mb: 2 }}>
            <Alert severity="error" variant="outlined">
              <Typography variant="subtitle2" fontWeight={600}>
                Motivo de Rechazo
              </Typography>
              <Typography variant="body2" sx={{ wordBreak: 'break-word', mt: 0.5 }}>
                {order.rejectionReason}
              </Typography>
            </Alert>
          </Box>
        )}

        {/* Row 1: Falabella (Fecha, ID, Estatus, Modalidad) | 99minutos (N° seguimiento) */}
        <Box
          display="flex"
          gap={2}
          width="100%"
          alignItems="stretch"
          flexDirection={{ xxs: 'column', md: 'row' }}
        >
          <Box flex={3}>
            <Typography
              color="text.secondary"
              fontWeight={700}
              display="block"
              textAlign="center"
              mb={1}
            >
              Falabella
            </Typography>
            <Grid container spacing={2} justifyContent="center">
              <Grid size={{ xxs: 12, sm: 4 }}>
                <PairTitle
                  title="Fecha de compra"
                  value={
                    order.createdAt
                      ? format(new Date(order.createdAt), 'dd/MM/yyyy')
                      : format(new Date(), 'dd/MM/yyyy')
                  }
                />
              </Grid>
              <Grid size={{ xxs: 12, sm: 4 }}>
                <PairTitle
                  title="ID orden"
                  value={order.orderFalabella || order.id}
                  isTitleGreen
                  isBold
                />
              </Grid>
              <Grid size={{ xxs: 12, sm: 4 }}>
                <PairTitle title="Estatus">
                  <StatusChip
                    status={getFalabellaStatusLabel(order.statusFalabella)}
                    color={getFalabellaStatusColor(order.statusFalabella)}
                    bg={getFalabellaStatusBg(order.statusFalabella)}
                  />
                </PairTitle>
              </Grid>
              <Grid size={{ xxs: 12, sm: 6 }}>
                <PairTitle
                  title={translation.t('modal.modality', 'Modalidad')}
                  value={getFalabellaModalityLabel(order.modality || order.shippingProviderType)}
                />
              </Grid>
              {order.lpn && order.lpn !== order.orderFalabella && (
                <Grid size={{ xxs: 12, sm: 6 }}>
                  <PairTitle title="LPN" value={order.lpn} />
                </Grid>
              )}
            </Grid>
          </Box>

          <Divider orientation="vertical" flexItem sx={{ display: { xxs: 'none', md: 'block' } }} />

          <Box flex={1} display="flex" flexDirection="column" alignItems="center">
            <Typography
              color="text.secondary"
              fontWeight={700}
              display="block"
              textAlign="center"
              mb={1}
            >
              99minutos
            </Typography>
            <PairTitle
              title="Nº de seguimiento"
              value={hasTracking ? order.tracking99m : 'Sin asignar'}
            />
          </Box>
        </Box>

        <Divider variant="fullWidth" sx={{ width: '100%', margin: '1.25rem 0' }} />

        {/* Row 2: Dirección de origen | Dirección de destino */}
        <Grid container spacing={2} width={1} justifyContent="center">
          <Grid size={{ xxs: 12, md: 6 }}>
            <PairTitle title="Dirección de origen" value={order.originAddress || 'No disponible'} />
          </Grid>
          <Grid size={{ xxs: 12, md: 6 }}>
            <PairTitle
              title="Dirección de destino"
              value={
                order.recipient?.address
                  ? `${order.recipient.address}${order.recipient.zipCode ? `, CP: ${order.recipient.zipCode}` : ''}`
                  : 'No disponible'
              }
            />
          </Grid>
        </Grid>

        {/* Row 3: Tabla de Productos */}
        {order.items && order.items.length > 0 && (
          <>
            <Divider variant="fullWidth" sx={{ width: '100%', margin: '1.25rem 0' }} />
            <Typography
              variant="subtitle2"
              fontWeight="bold"
              color={theme.palette.primary.main}
              align="center"
              mt={2}
            >
              Producto(s)
            </Typography>
            <Box mt={2} sx={{ width: '100%', overflowX: 'auto', paddingBottom: '1rem' }}>
              <Box
                component="table"
                sx={{
                  minWidth: 570,
                  width: '100%',
                  borderCollapse: 'collapse',
                  textAlign: 'center',
                  fontSize: '0.95rem',
                }}
              >
                <thead>
                  <tr style={{ backgroundColor: '#f9f9f9' }}>
                    <th style={cellStyleHeader}>Nombre del producto</th>
                    <th style={cellStyleHeader}>Valor de producto</th>
                    <th style={cellStyleHeader}>Moneda</th>
                    <th style={cellStyleHeader}>SKU</th>
                    <th style={cellStyleHeader}>Cantidad de productos</th>
                  </tr>
                </thead>
                <tbody>
                  {order.items.map((item, index) => (
                    <tr key={index} style={{ borderTop: '1px solid #eee' }}>
                      <td style={cellStyle}>{item.name || '-'}</td>
                      <td style={cellStyle}>
                        {item.price != null && item.price > 0
                          ? `$ ${item.price >= 1000 ? item.price.toLocaleString('en-US') : item.price}`
                          : '-'}
                      </td>
                      <td style={cellStyle}>{order.currency || 'CLP'}</td>
                      <td style={cellStyle}>{item.sku || '-'}</td>
                      <td style={cellStyle}>{item.quantity}</td>
                    </tr>
                  ))}
                </tbody>
              </Box>
            </Box>
          </>
        )}

        <Divider variant="fullWidth" sx={{ width: '100%', margin: '1.25rem 0' }} />

        {/* Row 4: Moneda | Costo de orden | Descargar etiqueta */}
        <Grid container spacing={2} width={1} justifyContent="center" alignItems="center">
          <Grid size={{ xxs: 12, md: 4 }}>
            <PairTitle title="Moneda" value={order.currency || 'CLP'} isBold isGreen />
          </Grid>
          <Grid size={{ xxs: 12, md: 4 }}>
            <PairTitle
              title="Costo de orden"
              value={
                order.amount != null
                  ? `$ ${
                      parseFloat(String(order.amount)) >= 1000
                        ? parseFloat(String(order.amount)).toLocaleString('en-US')
                        : order.amount
                    }`
                  : '-'
              }
              isBold
              isGreen
            />
          </Grid>
          <Grid
            size={{ xxs: 12, md: 4 }}
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={1}
          >
            {hasTracking ? (
              <Button variant="contained" onClick={() => onDownloadLabel(trackingId)}>
                Descargar etiqueta
              </Button>
            ) : onCreateOrder ? (
              <>
                <Button
                  variant="contained"
                  disabled={!canCreate || isCreating}
                  startIcon={
                    isCreating ? <CircularProgress size={16} color="inherit" /> : undefined
                  }
                  onClick={() => onCreateOrder(order.orderFalabella || order.id)}
                >
                  {isCreating ? 'Creando orden...' : 'Crear orden en 99minutos'}
                </Button>
                {!isDirect && (
                  <Alert
                    severity="warning"
                    variant="outlined"
                    sx={{
                      width: '100%',
                      py: 0.5,
                      px: 1,
                      fontSize: '0.75rem',
                      textAlign: 'center',
                    }}
                  >
                    {translation.t(
                      'modal.invalidOrderType',
                      'Tipo de orden no válida para creación en 99minutos (solo órdenes direct)',
                    )}
                  </Alert>
                )}
                {isDirect && isPending && !hasInternalKey && (
                  <Alert
                    severity="warning"
                    variant="outlined"
                    sx={{
                      width: '100%',
                      py: 0.5,
                      px: 1,
                      fontSize: '0.75rem',
                      textAlign: 'center',
                    }}
                  >
                    {translation.t(
                      'modal.pendingMissingInternalKey',
                      "Las órdenes en estatus 'Pendiente' requieren contar con internalKey (LPN) para su creación",
                    )}
                  </Alert>
                )}
                {isDirect && !isReadyToShip && !isPending && (
                  <Alert
                    severity="warning"
                    variant="outlined"
                    sx={{
                      width: '100%',
                      py: 0.5,
                      px: 1,
                      fontSize: '0.75rem',
                      textAlign: 'center',
                    }}
                  >
                    {translation.t(
                      'modal.invalidOrderStatus',
                      "Solo se permite crear órdenes en estatus 'Listo para envío' o 'Pendiente'",
                    )}
                  </Alert>
                )}
              </>
            ) : (
              <Button variant="contained" disabled>
                Descargar etiqueta
              </Button>
            )}
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
};

export default DetailsModal;
